import type { Agent } from '@deskworks/shared-types';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { NONE } from '@/components/desk-dialogs';
import { Field } from '@/components/field';
import { ModelField, supportsModel } from '@/components/model-field';
import { TagInput } from '@/components/tag-input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { useApiMutation, useRuntimeProfiles } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { profileLabel } from '@/lib/labels';
import { qk } from '@/lib/query-keys';

/** Create (no `agent`) or edit an Agent. */
export function AgentDialog({ workspaceId, agent, children }: { workspaceId: string; agent?: Agent; children: ReactNode }) {
  const navigate = useNavigate();
  const profiles = useRuntimeProfiles(workspaceId);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [instructions, setInstructions] = useState('');
  const [profileId, setProfileId] = useState(NONE);
  const [capabilities, setCapabilities] = useState<string[]>([]);
  const [model, setModel] = useState('');

  useEffect(() => {
    if (!open) return;
    setName(agent?.name ?? '');
    setDescription(agent?.description ?? '');
    setInstructions(agent?.instructions ?? '');
    setProfileId(agent?.runtimeProfileId ?? NONE);
    setCapabilities(agent?.capabilities ?? []);
    setModel(agent?.model ?? '');
  }, [open, agent]);

  const selectedProfile = profiles.data?.find((p) => p.id === profileId);

  const body = () => ({
    name: name.trim(),
    description: description.trim(),
    instructions,
    runtimeProfileId: profileId === NONE ? null : profileId,
    // The model belongs to the agent; empty = the runtime's default. The local demo runtime has no model.
    model: selectedProfile && supportsModel(selectedProfile.runtimeType) ? model.trim() || null : null,
    capabilities,
  });

  const save = useApiMutation(() => (agent ? api.agents.update(agent.id, body()) : api.agents.create(workspaceId, body())), {
    success: agent ? t('Agent saved') : t('Agent created'),
    invalidate: (a) => [qk.agents(workspaceId), qk.agent(a.id), qk.overview(workspaceId)],
    onSuccess: (a) => {
      setOpen(false);
      if (!agent) void navigate({ to: '/w/$workspaceId/agents/$agentId', params: { workspaceId, agentId: a.id } });
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) save.mutate();
  };


  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <form onSubmit={onSubmit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{agent ? t('Edit agent') : t('New agent')}</DialogTitle>
            <DialogDescription>
              {t('An agent is an AI team member with fixed instructions. You can use it on several desks.')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('Name')}>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('e.g. Case Agent')} autoFocus required />
            </Field>
            <Field label={t('Runtime')} hint={t('Automatic takes the free runtime with the lowest priority (Settings → Runtimes) and moves on when one is used up or can’t be reached. When none is free, it tries again an hour later.')}>
              <Select value={profileId} onValueChange={setProfileId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('Choose a runtime')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t('Automatic (recommended)')}</SelectItem>
                  {(profiles.data ?? []).map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {profileLabel(p)}
                      {!p.implemented && ` (${t('not supported yet')})`}
                      {p.status === 'disabled' && ` (${t('disabled')})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          {selectedProfile && supportsModel(selectedProfile.runtimeType) && (
            <ModelField
              className="-mt-1"
              value={model}
              onChange={setModel}
              profile={selectedProfile}
              hint={t('Leave empty to use the runtime’s default model.')}
            />
          )}
          {selectedProfile && selectedProfile.implemented && !selectedProfile.hasCredential && selectedProfile.runtimeType !== 'local' && (
            <p className="-mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              {selectedProfile.runtimeType === 'claude_subscription' || selectedProfile.runtimeType === 'codex_subscription'
                ? t('This subscription profile isn’t connected yet. Connect it under Settings → Runtimes, otherwise the agent can’t work.')
                : t('No API key is saved for this profile yet. Add it under Settings → Runtimes, otherwise the agent can’t work.')}
            </p>
          )}
          {selectedProfile && selectedProfile.status === 'disabled' && (
            <p className="-mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              {t('This profile is disabled. Enable it in Settings so the agent can work with it.')}
            </p>
          )}
          {selectedProfile && !selectedProfile.implemented && (
            <p className="-mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              {t('The worker doesn’t support this profile yet. The agent can’t work with it yet.')}
            </p>
          )}
          <Field label={t('Description')} optional hint={t('In short: what is this agent for?')}>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
          <Field label={t('Instructions')} hint={t('How the agent should work. Write it like an onboarding guide for a new colleague.')}>
            <Textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={10} className="font-mono text-[13px] leading-relaxed" />
          </Field>
          <Field
            label={t('Capabilities')}
            optional
            hint={t('Keywords that help the Lead Agent pick this agent, e.g. analysis, support, coding. Separate with Enter or a comma. Skills are chosen on the agent’s page.')}
          >
            <TagInput value={capabilities} onChange={setCapabilities} />
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button type="submit" loading={save.isPending} disabled={!name.trim()}>
              {agent ? t('Save') : t('Create agent')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
